import Service from '@root/common/base/Service';
import { ServiceInterface } from '@root/common/types/service';
import { QueryOptions } from '@apollo/client/core';

import { GetAuthor } from '@root/modules/author/types/author';
import getAuthorById from '@root/modules/author/graphql/getAuthorById.graphql';
import { dataLoader } from '@root/libs/redis/dataloader/dataLoader';

type ServiceVariables = GetAuthor['variables'];
type Response = GetAuthor['data'];
type FetchAuthorReturn = Response['authors']['data'][0];

export default class AuthorService extends Service implements ServiceInterface {
  private retriedFailedRequest = false;

  private async fetchAuthor(variables: ServiceVariables): Promise<FetchAuthorReturn | undefined> {
    try {
      const options = Object.assign({ query: getAuthorById }, { variables });
      const dataLoaderOptions = {
        remote: {
          keyPrefix: 'authorById',
        },
      };

      const requestWrapper = async (options: QueryOptions) => {
        const apiProvider = this.createProvider('GraphQL');
        apiProvider.selectAPI('content-api-v3').setLinkOptions({ useAutomaticPersistedQueries: true, useGETAutomaticPersistedQueries: true });

        const [response] = await apiProvider.query<Response>(options);

        return response.data;
      };

      const redisDataLoader = dataLoader<QueryOptions, Response>(requestWrapper, dataLoaderOptions);
      const response: Response = redisDataLoader ? await redisDataLoader.load(options) : await requestWrapper(options);

      if (!response.authors?.data.length) {
        this.handleError({ message: 'author.error.not_found', statusCode: 404 });
        return;
      }

      return response.authors.data[0];
    } catch (e) {
      // Retry failed request one time
      if (!this.retriedFailedRequest) {
        this.retriedFailedRequest = true;

        return await this.fetchAuthor(variables);
      }

      const error = this.generateErrorData(e);
      this.handleError(error);
    }
  }

  public async fetch(variables: ServiceVariables) {
    const response = await this.fetchAuthor(variables);

    return response;
  }
}
